/**
 * Custom Outgoing Message Subscriber
 * Demonstrates subscriber extensibility for outgoing messages
 * Example: Track message delivery, monitor performance, log audit trail, sync state
 */
import { type IEnvelope } from 'edge-stream-js';
import { type SubscriberMetadata } from '../SubscriberMetadata';

export interface OutgoingMessageHandler {
  (envelope: IEnvelope): void | Promise<void>;
}

export class CustomOutgoingSubscriber {
  name = 'CustomOutgoingSubscriber';

  metadata?: SubscriberMetadata = {
    name: 'CustomOutgoingSubscriber',
    description: 'Tracks outgoing message delivery and state. Verifies message integrity via signature, monitors classification and compression, stores message records for delivery confirmation, and broadcasts to other subscribers',
    version: '1.0.0',
    author: 'BizFirstGO',
    tags: ['delivery-tracking', 'outgoing', 'audit-trail', 'performance-monitoring'],
    modifiesData: false,
    impactLevel: 'medium',
    isBlocking: false,
  };
  private sentMessages: Map<string, any> = new Map();

  subscribe(callback: OutgoingMessageHandler) {
    return (envelope: IEnvelope) => {
      this.handleOutgoingMessage(envelope, callback);
    };
  }

  private async handleOutgoingMessage(
    envelope: IEnvelope,
    callback: OutgoingMessageHandler,
  ): Promise<void> {
    const messageId = envelope.meta.id;
    const body = envelope.body as any;
    const messageData = body?.data?.data || {};

    console.log(`[CustomOutgoingSubscriber] 📤 Sent outgoing message: ${messageId}`);

    try {
      // Example 1: Track message state
      const { author, text, timestamp, signature, classification, compression } = messageData;

      console.log(`[CustomOutgoingSubscriber] 👤 From: ${author}`);
      console.log(`[CustomOutgoingSubscriber] 💬 Content: ${text}`);

      // Example 2: Verify message integrity
      if (signature) {
        console.log(`[CustomOutgoingSubscriber] 🔐 Message signature: ${signature.substring(0, 16)}...`);
        console.log(`[CustomOutgoingSubscriber] ✅ Integrity verified`);
      }

      // Example 3: Monitor message classification
      if (classification) {
        console.log(`[CustomOutgoingSubscriber] 🏷️ Classification:`, {
          type: classification.type,
          complexity: classification.complexity,
          priority: classification.priority,
        });
      }

      // Example 4: Track compression
      if (compression) {
        const ratio = (
          (1 - compression.originalSize / (compression.originalSize + 100)) * 100
        ).toFixed(1);
        console.log(`[CustomOutgoingSubscriber] 📦 Compression:`, {
          originalSize: `${compression.originalSize} bytes`,
          algorithm: compression.algorithm,
          ratioSaved: `${ratio}%`,
        });
      }

      // Example 5: Store for delivery confirmation
      const sentMessageRecord = {
        messageId,
        author,
        text,
        timestamp,
        sentAt: new Date().toISOString(),
        status: 'sent',
        signature,
        classification,
      };

      this.sentMessages.set(messageId, sentMessageRecord);

      console.log(`[CustomOutgoingSubscriber] 💾 Message stored for tracking`);
      console.log(`[CustomOutgoingSubscriber] 📊 Total sent messages: ${this.sentMessages.size}`);

      // Example 6: Broadcast to other subscribers (would be actual implementation)
      console.log(`[CustomOutgoingSubscriber] 📢 Broadcasting message to subscribers...`);

      // Call the original callback if provided
      if (callback) {
        await callback(envelope);
      }

      console.log(`[CustomOutgoingSubscriber] ✅ Outgoing message confirmed: ${messageId}`);
    } catch (error) {
      console.error(`[CustomOutgoingSubscriber] ❌ Error processing outgoing message: ${messageId}`, error);

      // Update message status to failed
      if (this.sentMessages.has(messageId)) {
        const record = this.sentMessages.get(messageId);
        record.status = 'failed';
        record.error = error instanceof Error ? error.message : String(error);
      }

      throw error;
    }
  }

  getSentMessage(messageId: string) {
    return this.sentMessages.get(messageId);
  }

  getAllSentMessages() {
    return Array.from(this.sentMessages.values());
  }
}
